import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  CheckCircle2, 
  Loader2, 
  UserPlus, 
  AlertTriangle,
  Info,
  Send,
  SkipForward
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface JourneyMemberInvitationStepProps {
  stepId: string;
  userId: string;
  isCompleted: boolean;
  actionConfig: Record<string, any>;
  onComplete: () => void;
}

// Role options for selection
const ROLE_OPTIONS = [
  { value: "treasury_secretary", label: "Secretary Treasurer", description: "Full administrative access and document control" },
  { value: "managing_member", label: "Managing Member", description: "Voting rights and governance participation" },
  { value: "board_member_active", label: "Board Member", description: "Active board participant with voting rights" },
];

export function JourneyMemberInvitationStep({
  stepId,
  userId,
  isCompleted,
  actionConfig,
  onComplete,
}: JourneyMemberInvitationStepProps) {
  const [memberName, setMemberName] = useState("");
  const [memberEmail, setMemberEmail] = useState("");
  const [selectedRole, setSelectedRole] = useState<string>("treasury_secretary");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [invitationSent, setInvitationSent] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch user's UNA
  const { data: una, isLoading: unaLoading } = useQuery({
    queryKey: ['user-una', userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('unas')
        .select('id, name, primary_officer_role, treasury_secretary_id')
        .eq('treasury_secretary_id', userId)
        .maybeSingle();
      
      if (error) throw error;
      return data;
    },
  });

  // Auto-complete this step if user is a Secretary Treasurer
  useEffect(() => {
    if (!unaLoading && una) {
      const primaryRole = una.primary_officer_role?.toLowerCase();
      if (primaryRole === 'treasury secretary') {
        // Auto-complete this step for Treasury Secretaries
        handleAutoComplete();
      }
    }
  }, [una, unaLoading]);

  const handleAutoComplete = async () => {
    try {
      // Mark step as completed (skip)
      await supabase
        .from('user_journey_step_progress')
        .upsert({
          user_id: userId,
          step_id: stepId,
          status: 'completed',
          completed_at: new Date().toISOString(),
          action_data: { skipped: true, reason: 'treasury_secretary_auto_skip' },
        }, { onConflict: 'user_id,step_id' });

      // Unlock next step
      const { data: currentStep } = await supabase
        .from('journey_steps')
        .select('step_number')
        .eq('id', stepId)
        .single();

      if (currentStep) {
        const { data: nextStep } = await supabase
          .from('journey_steps')
          .select('id')
          .eq('step_number', currentStep.step_number + 1)
          .eq('is_active', true)
          .single();

        if (nextStep) {
          await supabase
            .from('user_journey_step_progress')
            .upsert({
              user_id: userId,
              step_id: nextStep.id,
              status: 'active',
              started_at: new Date().toISOString(),
            }, { onConflict: 'user_id,step_id' });
        }
      }

      queryClient.invalidateQueries({ queryKey: ['journey-steps-progress'] });
      onComplete();
    } catch (error) {
      console.error('Error auto-completing step:', error);
    }
  };

  const handleInvite = async () => {
    if (!memberName.trim() || !memberEmail.trim() || !una) {
      toast({
        title: "Missing Information",
        description: "Please fill in all fields",
        variant: "destructive",
      });
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(memberEmail)) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // 1. Create una_member record with pending status
      const memberInsertData = {
        una_id: una.id,
        full_name: memberName.trim(),
        email: memberEmail.trim().toLowerCase(),
        member_role: selectedRole,
        role: selectedRole.replace(/_/g, ' '), // Legacy role field
        status: 'pending',
      };
      
      const { data: newMember, error: memberError } = await supabase
        .from('una_members')
        .insert([memberInsertData] as any)
        .select()
        .single();

      if (memberError) {
        // Check if member already exists
        if (memberError.code === '23505') {
          throw new Error('A member with this email already exists');
        }
        throw memberError;
      }

      // 2. Create member invitation record
      const token = crypto.randomUUID() + "-" + Date.now();
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7);

      const { error: inviteError } = await supabase
        .from('member_invitations')
        .insert([{
          una_id: una.id,
          una_member_id: newMember.id,
          invited_by: userId,
          invitation_token: token,
          email: memberEmail.trim().toLowerCase(),
          member_role: selectedRole as any,
          expires_at: expiresAt.toISOString(),
        }]);

      if (inviteError) throw inviteError;

      // 3. Send invitation email via edge function
      const { error: sendError } = await supabase.functions.invoke('send-member-invitation', {
        body: {
          memberEmail: memberEmail.trim().toLowerCase(),
          memberName: memberName.trim(),
          memberRole: selectedRole,
          unaName: una.name,
          invitationToken: token,
          invitedByName: 'Your UNA Administrator',
        },
      });

      if (sendError) {
        console.error('Error sending invitation email:', sendError);
        // Don't fail the whole process if email fails
        toast({
          title: "Invitation Created",
          description: "Member added but there was an issue sending the email. They can still be invited from the Members page.",
          variant: "default",
        });
      } else {
        toast({
          title: "Invitation Sent!",
          description: `An invitation has been sent to ${memberEmail}`,
        });
      }

      // 4. Log to audit trail
      await supabase.from('una_audit_log').insert([{
        una_id: una.id,
        user_id: userId,
        action: 'member_invited',
        resource_type: 'una_member',
        resource_id: newMember.id,
        details: {
          member_name: memberName.trim(),
          member_email: memberEmail.trim().toLowerCase(),
          member_role: selectedRole,
          source: 'journey_step_9',
        },
      }] as any);

      // 5. Create journal record for governance documentation
      await supabase.from('una_journal_records').insert([{
        una_id: una.id,
        record_date: new Date().toISOString().split('T')[0],
        record_type: 'Member Appointment',
        record_category: 'governance',
        title: `Member Invitation: ${memberName.trim()}`,
        description: `Invited ${memberName.trim()} (${memberEmail.trim().toLowerCase()}) to join the UNA as ${selectedRole.replace(/_/g, ' ')}.`,
        created_by: userId,
      }] as any);

      setInvitationSent(true);

      // 6. Mark step as completed
      await supabase
        .from('user_journey_step_progress')
        .upsert({
          user_id: userId,
          step_id: stepId,
          status: 'completed',
          completed_at: new Date().toISOString(),
          action_data: { 
            invitation_sent: true,
            member_id: newMember.id,
            member_email: memberEmail.trim().toLowerCase(),
          },
        }, { onConflict: 'user_id,step_id' });

      // 7. Unlock next step
      const { data: currentStep } = await supabase
        .from('journey_steps')
        .select('step_number')
        .eq('id', stepId)
        .single();

      if (currentStep) {
        const { data: nextStep } = await supabase
          .from('journey_steps')
          .select('id')
          .eq('step_number', currentStep.step_number + 1)
          .eq('is_active', true)
          .single();

        if (nextStep) {
          await supabase
            .from('user_journey_step_progress')
            .upsert({
              user_id: userId,
              step_id: nextStep.id,
              status: 'active',
              started_at: new Date().toISOString(),
            }, { onConflict: 'user_id,step_id' });
        }
      }

      queryClient.invalidateQueries({ queryKey: ['journey-steps-progress'] });
      onComplete();

    } catch (error: any) {
      console.error('Error inviting member:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to send invitation",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = async () => {
    setIsSubmitting(true);
    try {
      // Mark step as completed with skip flag
      await supabase
        .from('user_journey_step_progress')
        .upsert({
          user_id: userId,
          step_id: stepId,
          status: 'completed',
          completed_at: new Date().toISOString(),
          action_data: { skipped: true, reason: 'user_skipped' },
        }, { onConflict: 'user_id,step_id' });

      // Unlock next step
      const { data: currentStep } = await supabase
        .from('journey_steps')
        .select('step_number')
        .eq('id', stepId)
        .single();

      if (currentStep) {
        const { data: nextStep } = await supabase
          .from('journey_steps')
          .select('id')
          .eq('step_number', currentStep.step_number + 1)
          .eq('is_active', true)
          .single();

        if (nextStep) {
          await supabase
            .from('user_journey_step_progress')
            .upsert({
              user_id: userId,
              step_id: nextStep.id,
              status: 'active',
              started_at: new Date().toISOString(),
            }, { onConflict: 'user_id,step_id' });
        }
      }

      toast({
        title: "Step Skipped",
        description: "You can add members later from UNA Governance.",
      });

      queryClient.invalidateQueries({ queryKey: ['journey-steps-progress'] });
      onComplete();
    } catch (error: any) {
      console.error('Error skipping step:', error);
      toast({
        title: "Error",
        description: "Failed to skip step",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isCompleted || invitationSent) {
    return (
      <div className="flex items-center justify-center py-4 text-green-600">
        <CheckCircle2 className="w-6 h-6 mr-2" />
        <span className="font-medium">
          {invitationSent ? "Invitation sent!" : "Step completed"}
        </span>
      </div>
    );
  }

  if (unaLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  // This step should only show for non-Secretary Treasurer roles
  // The auto-complete useEffect handles Treasury Secretaries
  const primaryRole = una?.primary_officer_role || 'Unknown';

  return (
    <div className="space-y-6">
      {/* Explanation */}
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          As {primaryRole}, you need at least one other officer (such as a Treasurer or Secretary) 
          to properly conduct UNA governance meetings. Inviting a trusted person now ensures your 
          meetings will be valid.
        </AlertDescription>
      </Alert>

      {/* Warning about skipping */}
      <Alert variant="destructive" className="bg-amber-50 border-amber-200 text-amber-800">
        <AlertTriangle className="h-4 w-4 text-amber-600" />
        <AlertDescription className="text-amber-800">
          Without a second officer, you may need to act as both {primaryRole} and Secretary pro tem 
          for meetings, which limits governance accountability.
        </AlertDescription>
      </Alert>

      {/* Invitation Form */}
      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="flex items-center gap-2 mb-4">
            <UserPlus className="w-5 h-5 text-primary" />
            <h3 className="font-semibold">Invite Your First Officer</h3>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="memberName">Full Name</Label>
              <Input
                id="memberName"
                placeholder="Enter member's full name"
                value={memberName}
                onChange={(e) => setMemberName(e.target.value)}
                disabled={isSubmitting}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="memberEmail">Email Address</Label>
              <Input
                id="memberEmail"
                type="email"
                placeholder="Enter member's email"
                value={memberEmail}
                onChange={(e) => setMemberEmail(e.target.value)}
                disabled={isSubmitting}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="memberRole">Role</Label>
              <Select
                value={selectedRole}
                onValueChange={setSelectedRole}
                disabled={isSubmitting}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      <div className="flex flex-col">
                        <span>{role.label}</span>
                        <span className="text-xs text-muted-foreground">{role.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              onClick={handleInvite}
              disabled={isSubmitting || !memberName.trim() || !memberEmail.trim()}
              className="flex-1"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Invitation
                </>
              )}
            </Button>

            {actionConfig?.allow_skip && (
              <Button
                variant="outline"
                onClick={handleSkip}
                disabled={isSubmitting}
              >
                <SkipForward className="w-4 h-4 mr-2" />
                {actionConfig?.skip_button_text || "Skip for now"}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
